import { abLegStops, cabinType, escapeAttr, favouriteStatus, getEmissionText, getFlightEmissionsData, getPromotedValue, getTripType, insertIf, isSavedAsFavorite, join, moreInfoAgencyCount, passengersText, renderIndex, ticketTypeIcon, tripGetToAndFromCity } from '@js/components/resultsRenderHelpers';
import { getAgencyImagePath, getTrackingClass, l, lc, linkPrefix, lreplace } from '@utils/toolbox';
import { resultsRender } from './resultsRender';

/**
 * Renders a list of journeys
 * @param {Array} journeys - The list of journeys to render
 * @param {Object} options - Options for rendering
 * @param {boolean} options.showEmissions - Whether to show emissions
 * @param {boolean} options.isFavoritePage - Whether the page is a favorite page
 * @param {boolean} options.displayFavBtn - Whether to display the favorite button
 * @param {boolean} options.multiplePassengers - Number of passengers
 * @param {boolean} options.preview - Whether to show a preview
 * @param {Object} options.filters - Filters for rendering
 * @param {boolean} options.filters.flexTicket - Whether to show flex ticket
 * @param {boolean} options.filters.baggageIncluded - Whether to show baggage included
 * @param {boolean} options.filters.showEmissions - Whether to show emissions
 * @param {boolean} options.filters.isSortedByNet - Whether to show sorted by net
 * @param {boolean} options.filters.isEmissionAbnormallyHigh - Whether to show emission abnormally high
 *
*/
export function renderJourneys (journeys, options) {
	return join(journeys, journey => renderJourney(journey, options));
}

function renderJourney (journey, options) {
	const baseClasses = [
		'abResult',
		`abResult--${getTripType(journey)}`,
		favouriteStatus(journey),
		options.preview ? 'abResult--preview' : '',
		journey.bestValue ? `abResult--${journey.bestValue}Best` : '',
		journey.shared ? 'shared-result' : ''
	]
		.filter(Boolean)
		.join(' ');

	const resultIndex = journey.resultIndex;

	const agents = resultsRender.response.flights[resultIndex].agents;

	const source = window?.showresult?.activeView || 'list';

	return `
<div class="${baseClasses}"
	data-index="${resultIndex}"
	${insertIf(journey.favouriteMeta?.isChanging, 'data-status="removing"')}
	tabindex="0"
	aria-label="${escapeAttr(journey.ariaLabel)}"
>
	${insertIf(options.showEmissions, () => renderEmissionBlock(journey))}
	<div class="abResult__main" data-action="toggle-trip-details">
		${renderMainHeader(journey, options, resultIndex)}

		<div class="abResult__legs abResult__legs--default">
			${join(journey.trips, t => renderTrip(t))}
		</div>

		${renderSummaries(journey, options)}
		<div class="abResult__flightDetails"></div>
	</div>

	${renderResultAgents({
		resultIndex: resultIndex,
		agents: agents,
		promotedProduct: resultsRender.response.flights[resultIndex].promotedProduct,
		promotedAgent: resultsRender.response.flights[resultIndex].promotedAgent,
		hasPromotedAgent: ['promotedAgent', 'promotedProduct'].every(key => key in resultsRender.response.flights[resultIndex]),
		extended: false,
		source: source
	})}

	<div class="abResult__otaAndBuyAll"></div>

${journey.favouriteMeta?.isPast
		? `<div class="pastResult--content" data-action="dismiss"><span>${l('fav.ui.isOldRecord')}</span></div>`
		: journey.favouriteMeta?.isOld
			? `<div class="oldResult--content" data-action="refresh"><span>${l('fav.ui.needsRefresh')}<span class="icon-loading"></span></span></div>`
			: ''
	}
</div>`;
}

function renderEmissionBlock (journey) {
	if (!journey) {
		return '';
	}

	const {
		isFullyCompensated,
		isEmissionAbnormallyHigh,
		hasAirlineCompensation,
		airlineCompensation,
		userCompensation,
		emissionLegend,
		emissionValueWithUnit,
		emissionDefinition,
		isSortedByNet,
		emissionValue
	} = getFlightEmissionsData(journey.raw_emission, journey.compensated_emission, journey.emission);


	const generateUserCompensationClasses = () => {
		const classes = ['compensationDistribution__arrow', 'compensationDistribution__arrow--user'];
		if (!isFullyCompensated) {
			classes.push('compensationDistribution__arrow--visible');
		}
		if (!hasAirlineCompensation) {
			classes.push('compensationDistribution__arrow--unique');
		}
		return classes.join(' ');
	};

	return `
<div class="abResult__emission" data-emission="${emissionValue}">
	<div class="emission__header emission__row">
		<div class="emission__col emission__col--climateCompensation">
		<span class="emission__title">${getEmissionText(journey) ?? ''}</span>
		</div>
	</div>

	<div class="emission__main emission__row ${insertIf(isEmissionAbnormallyHigh, 'emission--error')}">
		<div class="emission__col emission__col--carbonFootprint">
			<img src="${linkPrefix.assetPrefix}/images/global/CO2-cloud.png" alt="">
			<span>${emissionValueWithUnit}${insertIf(isSortedByNet, '*')}</span>
		</div>
		<div class="emission__col emission__col--climateCompensation compensationDistribution${insertIf(!isFullyCompensated, ' compensation-user')}" data-airline="${airlineCompensation}" data-user="${userCompensation}">
		<div class="compensationDistribution__arrow compensationDistribution__arrow--airline${insertIf(hasAirlineCompensation, ' compensationDistribution__arrow--visible')}" style="width:${airlineCompensation}%"></div>
		<div class="${generateUserCompensationClasses()}" ${insertIf(!isFullyCompensated, () => `style="width:${userCompensation}%"`)}></div>

		<div class="compensationDistribution__legend">
			${emissionLegend ?? ''}
		</div>
		</div>
	</div>

	${insertIf(isSortedByNet, () => `<div class="emission__footer emission__row">${emissionDefinition ?? ''}</div>`)}
</div>`;
}

function renderMainHeader (journey, options, index) {
	/* variant for favourite page vs normal search */
	if (options.isFavoritePage) {
		return `
<div class="abResult__airlineAndMoreInfo">
	<span class="tripIndex">${renderIndex(index)}</span>
	${renderFavouriteHeader(journey)}
	<div class="tools">
		<span class="icon-info" data-action="info"></span>
		<span class="icon-delete" data-action="delete"></span>
		<span class="icon icon-heart-full" data-action="delete"></span>
	</div>
</div>`;
	}

	return `
<div class="abResult__airlineAndMoreInfo">
	<span>${journey.airlines}</span>
	${journey.agents.length
		? `<span>${moreInfoAgencyCount(journey.agents.length)}</span>`
		: `<span>${l('showresult.result.showMoreInfo')}</span>`
	}
	${insertIf(options.displayShareButton, () => `
	<div class="abResult__shareBtn abBtn abBtn--flex" data-result="${index}">
		<span>${l('global.simplePhrases.share')}</span>
		<span class="icon icon-share-2 icon-with-tooltip" data-tooltip="${l('global.simplePhrases.share')}"></span>
	</div>
	`)}

	<div class="abResult__flightDetailsBtn">
		<span>${l('showresult.result.showMoreInfo')}</span>
	</div>

${insertIf(options.displayFavBtn, () => `
	<div class="abResult__flightFavBtn ${isSavedAsFavorite(journey)}">
		<span class="icon icon-heart-empty"></span>
		<span class="icon icon-heart-full"></span>
	</div>`)}
</div>
`;
}

function renderFavouriteHeader (journey) {
	let left = `<div class="tripDestinations">`;
	let right = `<div class="tripDates">`;

	const tripType = getTripType(journey);
	const { fromCity: departureFromCity, toCity: departureToCity } = tripGetToAndFromCity(journey.trips[0]);

	if (tripType === 'return') {
		const { fromCity: returnFromCity } = tripGetToAndFromCity(journey.trips[journey.trips.length - 1]);
		left += `<span class="tripDestination">${departureFromCity}<span class='icon-switch'></span>${returnFromCity}</span>`;
	} else if (tripType === 'oneway') {
		left += `<span class="tripDestination">${departureFromCity}<span class='icon-arrow-right'></span>${departureToCity}</span>`;
	} else if (tripType === 'openjaw') {
		for (const trip of journey.trips) {
			const { fromCity, toCity } = tripGetToAndFromCity(trip);
			left += `<span class="tripDestination">${fromCity}<span class='icon-arrow-right'></span>${toCity}</span>`;
		}
	}
	right += `<span class="tripDate"><span class="tripDate--title">${l('global.simplePhrases.depart')}</span> ${journey.trips[0].segment[0].localizedArrivalDayShortString}</span>`;
	right += `<span class="tripDate"><span class="tripDate--title">${(tripType === 'oneway') ? l('global.simplePhrases.arrival') : l('global.simplePhrases.return')}</span> ${journey.trips[journey.trips.length - 1].segment[journey.trips[journey.trips.length - 1].segment.length - 1].localizedArrivalDayShortString}</span>`;

	return left + `</div>` + right + `</div>`;
}


function renderTrip (trip) {
	return `
<div class="abResult__leg">
	${join(trip?.segment, (segment, index, originalArray) => renderSegment(segment, index, originalArray.length, trip))}
</div>`;
}

/**
 *
 * @param {Object} segment
 * @param {Number} index
 * @param {Number} length
 * @param {Object} trip
 * @returns
 */
function renderSegment (segment, index, length, trip) {
	const first = index === 0;
	const last = index === length - 1;
	let html = '';

	/* first-segment: airline logo + depart time */
	if (first) {
		html += `
		<div class="abResult__icao">
		  <img src="${linkPrefix.assetPrefix}/images/airlines/iata/small/${trip.airlineCode}.png"
				class="abResult__airlineImg"
				title="${trip.airline}"
				alt="${trip.airline}"
			>
		</div>
		<div class="abResult__depart"><span>${segment.departureTime}</span></div>
  
		<div class="abResult__iatas${trip.mixedCabinType ? ' combined-cabin' : ''}">
		  <div class="abIataTimespan ${segment.requiresAirportChange ? 'abIataTimespan--red' : `abIataTimespan--${trip.waitingTimeStatus}`}">
			<div class="abIataTimespan__iata depart">
			  <span>${segment.departureAirport}</span>
			</div>
  
			<div class="abIataTimespan__time">
			  <div class="abIataTimespan__time--decoration-up">
				<span>${trip.totalTravelTime}</span>
			  </div>
			  ${join(trip?.waiting?.airports, airport => `<div class="abIataTimespan__dot" style="left:${airport.percent}%"></div>`)}
			  <div class="abIataTimespan__time--decoration-down">
				<span>${abLegStops(trip?.waiting?.stops)}</span>
				<span class="abIataTimespan__time--decoration--icon icon icon-circle-question-mark" title="${l('showresult.tooltip.problemWithLeg')}"></span>
			  </div>
			</div>`;
	}

	/* last-segment arrival airport + closing tags + arrival time */
	if (last) {
		html += `
			<div class="abIataTimespan__iata arrive">
			  <span>${segment.arrivalAirport}</span>
			</div>
		  </div> 
		</div>
  
		<div class="abResult__return"><span>${segment.arrivalTime}</span></div>`;
	}

	return html;
}

/**
 *
 * @param {Object} journey
 * @param {Object} options
 * @param {boolean} options.multiplePassengers
 * @param {boolean} options.isFavoritePage
 * @param {boolean} options.filters.flexTicket
 * @param {boolean} options.filters.baggageIncluded
 * @returns {String} - html
 */
function renderSummaries (journey, options) {
	return `
<div class="abResult__summaries">
	<div class="abResult__segmentInfo">
		<div class="abResult__summary">
		<span class="${ticketTypeIcon(journey.cabin)}"></span>
		<span>${cabinType(journey.cabin)}</span>
	</div>

	${insertIf(options.multiplePassengers, () =>
	`<div class="abResult__summary">
		<span class="icon icon-three-users"></span>
		<span>${lreplace('showresult.result.pricePerPassengers', { passengers: options.multiplePassengers })}</span>
	</div>`
	)}

	${insertIf(options.filters?.flexTicket, () =>
	`<div class="abResult__summary">
		<span class="icon icon-rebooking" style="font-weight:900"></span>
		<span>${l('global.simplePhrases.rebooking')}</span>
	</div>`
	)}

	${insertIf(options.filters?.baggageIncluded, () =>
	`<div class="abResult__summary">
		<span class="icon icon-checkin-luggage"></span>
		<span>${lreplace('showresult.result.checkedInBagsIncluded', { bags: options?.filters?.baggageIncluded })}</span>
	</div>`
	)}
 
	${insertIf(options.isFavoritePage, () =>
	`<div class="abResult__summary">
		<span class="icon-person-luggage"></span>
		<span>${passengersText(journey)}</span>
	</div>
	<div class="abResult__summary">
		<span class="icon-plane"></span>
		<span>${journey.airlines}</span>
	</div>`
	)}
	</div>
</div>`;
}


/**
 * @param {Object} opts
 * @param {Array} opts.agents
 * @param {Number} opts.climateCompensationPrice
 * @param {Boolean} opts.extended
 * @param {Boolean} opts.hasPromotedAgent
 * @param {Boolean} opts.isModal
 * @param {String} opts.promotedAgent
 * @param {Number} opts.resultIndex
 * @returns {String} - html
 */
export function renderResultAgents (opts) {
	const {
		agents,
		climateCompensationPrice,
		extended,
		hasPromotedAgent,
		isModal,
		promotedAgent,
		resultIndex,
		source,
		promotedProduct
	} = opts;

	// Loop over agents
	const html = agents.map((agentData, agentIndex) => {
		const { agent, promoted, directBooking, recommended, code, price, availableExtraProducts } = agentData;
		let { bookingUrl } = agentData;

		bookingUrl = `${linkPrefix.base}${bookingUrl}`;

		let btnPrice = lc(price);
		const target = directBooking ? '_self' : '_blank';

		const promotedValue = getPromotedValue(promotedProduct);
		const isPromotedAgent = (hasPromotedAgent && agent === promotedAgent && agentIndex === 1) || promoted;

		if (extended) {
			// Skip the first agent that is already rendered in the non extended view
			if (agentIndex === 0) {
				return;
			}

			// Skip the second agent if such has already been rendered (promoted as best of flex agent)
			if (agentIndex === 1 && hasPromotedAgent) {
				return;
			}
		} else {
			// No more than 2 agents can be displayed in the non extended view
			if (agentIndex > 1) {
				return;
			}

			// Only add a promoted agent
			if (agentIndex === 1 && !hasPromotedAgent) {
				return;
			}
		}

		if (isPromotedAgent) {
			switch (promotedValue) {
				case 'flexTicket': {
					const rebookingIndex = availableExtraProducts.rebooking;

					let rebookingPrice = 0;
					if (resultsRender.response.uniqueRebooking) {
						rebookingPrice = resultsRender.response.uniqueRebooking[rebookingIndex];
					} else {
						rebookingPrice = parseFloat(rebookingIndex);
					}
					btnPrice = lc(price + rebookingPrice);
					break;
				}

				case 'baggageIncluded': {
					const baggageIndex = availableExtraProducts.baggage;

					let baggagePrice = 0;
					if (resultsRender.response.uniqueBaggage) {
						baggagePrice = parseFloat(resultsRender.response.uniqueBaggage[baggageIndex].cost) * resultsRender.numberOfBags;
					} else {
						baggagePrice = parseFloat(baggageIndex);
					}
					btnPrice = lc(price + baggagePrice);
					break;
				}

				case 'cabinBaggageIncluded': {
					const cabinBaggageIndex = availableExtraProducts.cabinBaggage;

					let cabinBaggagePrice = 0;
					if (resultsRender.response.uniqueCabinBaggage) {
						cabinBaggagePrice = parseFloat(resultsRender.response.uniqueCabinBaggage[cabinBaggageIndex].cost) * resultsRender.numberOfBags;
					} else {
						cabinBaggagePrice = parseFloat(cabinBaggagePrice);
					}

					btnPrice = lc(price + cabinBaggagePrice);
					break;
				}

				case 'carbonOffset': {
					btnPrice = lc(price + climateCompensationPrice);
					break;
				}
			}
		}

		// Add outclick flag to youth ticket
		if (resultsRender.isYouthSearch && resultsRender.isYouthFiltered) {
			bookingUrl += '&youthTicket=1';
		}

		if (isPromotedAgent) {
			bookingUrl += '&button=promoted';
		}

		if ((isPromotedAgent && promotedValue === 'flexTicket') || resultsRender.isFlexTicketFiltered) {
			bookingUrl = /\bflexTicket\b/.test(bookingUrl)
				? bookingUrl
				: `${bookingUrl}&flexTicket=1`;
		}

		if ((isPromotedAgent && promotedValue === 'baggageIncluded') || resultsRender.isBaggageIncludedFiltered) {
			bookingUrl = /\bbaggageIncluded\b/.test(bookingUrl)
				? bookingUrl
				: `${bookingUrl}&baggageIncluded=${resultsRender.numberOfBags}`;
		}

		if ((isPromotedAgent && promotedValue === 'cabinBaggageIncluded') || resultsRender.isCabinBaggageIncludedFiltered) {
			bookingUrl = /\bcabinBaggageIncluded\b/.test(bookingUrl)
				? bookingUrl
				: `${bookingUrl}&cabinBaggageIncluded=${resultsRender.numberOfBags}`;
		}

		if (js_params.controllerClass === 'page\\favorite') {
			bookingUrl += '&favourite=1';
		}

		bookingUrl += `&source=${source}`;

		const isRecommendedVisible = agentIndex === 0 && !hasPromotedAgent;
		const isRecommended = recommended && (extended || isRecommendedVisible);

		// Visually hide the QuickAndEasy label for bast agent only while preserving promotion as is
		const agentLabel = getAgentLabel(isPromotedAgent, directBooking, isRecommended);

		const { styles, btnStyles, innerStyles } = getResultAgentStyles({
			agent: agent,
			agentIndex: agentIndex,
			agentLabel: agentLabel,
			hasPromotedAgent: hasPromotedAgent,
			isPromotedAgent: isPromotedAgent,
			promotedValue: promotedValue,
			resultIndex: resultIndex,
			source: source
		});

		const stats = renderResultAgentStats({
			agents: agents,
			extended: extended,
			hasPromotedAgent: hasPromotedAgent,
			index: agentIndex,
			isModal: isModal
		});


		// Get the aria-label for the buy button
		let agentText = '';
		if ((isPromotedAgent && promotedProduct === 'cabinBaggage') || agentLabel === 'cabinBaggage') {
			agentText += ' + ' + l(' global.simplePhrases.cabinBaggageIncluded');
		} else if ((isPromotedAgent && promotedProduct === 'baggage') || agentLabel === 'baggage') {
			agentText += ' + ' + l('global.simplePhrases.baggageIncluded');
		} else if ((isPromotedAgent && availableExtraProducts?.rebooking) || agentLabel === 'flex') {
			agentText += ' + ' + l('global.simplePhrases.rebooking');
		}
		const arialabel = agent + ': ' + btnPrice + agentText;

		return `
<div class="${styles}">
	<div class="abResult__viewAllAgencies" data-action="view-all-agencies">
		${stats}
	</div>
	<a href="${bookingUrl}"
		class="${btnStyles}"
		target="${target}"
		rel="nofollow noopener"
		aria-label="${arialabel}"
		tabindex="-1">
		<div class="${innerStyles}">
			${renderResultAgentLabel(agentLabel, promotedValue)}
			<div class="abBuyBtn__previewAgency">
				<img src="${getAgencyImagePath({ code })}"
					data-agent="${code}"
					class="agency-image"
					width="90"
					height="25"
					alt=""
				/>
			</div>
			<div class="abBuyBtn__previewPrice">
				<span class="abBuyBtn__previewPrice--text">${l('showresult.result.goToTrip')}</span>
				<span>${btnPrice}</span>
			</div>
		</div>
	</a>
</div>`;
	});

	return html.join('');
}

/**
 *
 * @param {Object} options
 * @param {String} options.agent
 * @param {Number} options.agentIndex
 * @param {String} options.agentLabel
 * @param {Boolean} options.hasPromotedAgent
 * @param {Boolean} options.isPromotedAgent
 * @param {String|undefined} options.promotedValue
 * @param {Number} options.resultIndex
 */
function getResultAgentStyles (options) {
	const { agent, agentIndex, agentLabel, hasPromotedAgent, isPromotedAgent, promotedValue, resultIndex } = options;
	const styles = ['abResult__otaAndBuyPreview'];
	const btnStyles = ['otaLink', getTrackingClass(`agent-${agent}`), getTrackingClass(`result${parseInt(resultIndex) + 1}`), getTrackingClass(`ota${parseInt(agentIndex) + 1}`)];
	const innerStyles = ['abBuyBtn', 'abBuyBtn--main'];

	if (hasPromotedAgent) {
		if (agentIndex === 0) {
			btnStyles.push(getTrackingClass('otaPrimary'));
		}

		if (isPromotedAgent) {
			btnStyles.push(getTrackingClass('otaSecondary'));
		}
	}

	switch (agentLabel) {
		case 'best':
			styles.push('abResult__otaAndBuyPreview--best');
			innerStyles.push('abBuyBtn--showBest');
			btnStyles.push(getTrackingClass(`label-${promotedValue}`));
			break;

		case 'flex':
			styles.push('abResult__otaAndBuyPreview--flex');
			innerStyles.push('abBuyBtn--showBest');
			btnStyles.push(getTrackingClass(`label-flexTicket`));
			break;

		case 'baggage':
			styles.push('abResult__otaAndBuyPreview--baggage');
			innerStyles.push('abBuyBtn--showBest');
			btnStyles.push(getTrackingClass('label-baggageIncluded'));
			break;

		case 'cabinBaggage':
			styles.push('abResult__otaAndBuyPreview--cabinBaggage');
			innerStyles.push('abBuyBtn--showBest');
			btnStyles.push(getTrackingClass('label-cabinBaggageIncluded'));
			break;

		case 'recommended':
			innerStyles.push('abBuyBtn--showRecommended');
			btnStyles.push(getTrackingClass('label-recommended'));
			break;
	}

	// Disable buy button if search date is too old
	if (js_params.searchDateIsTooOld && !(['pricecalendar'].includes(options.source))) {
		btnStyles.push('otaLink--disabled');
	}

	const agentStyles = {
		btnStyles: btnStyles.join(' '),
		innerStyles: innerStyles.join(' '),
		styles: styles.join(' ')
	};

	return agentStyles;
}

/**
 *
 * @param {Boolean} isPromotedAgent
 * @param {Boolean} isDirectBooking
 * @param {Boolean} isRecommended
 * @param {Boolean} isFlexTicketFiltered
 * @param {Boolean} isBaggageIncludedFiltered
 * @param {Boolean} isCabinBaggageIncludedFiltered
 * @returns
 */
function getAgentLabel (isPromotedAgent, isDirectBooking, isRecommended) {
	if (isPromotedAgent) {
		return 'best';
	} else if (resultsRender.isFlexTicketFiltered) {
		return 'flex';
	} else if (resultsRender.isBaggageIncludedFiltered) {
		return 'baggage';
	} else if (resultsRender.isCabinBaggageIncludedFiltered) {
		return 'cabinBaggage';
	} else if (isDirectBooking) {
		return 'express';
	} else if (isRecommended) {
		return 'recommended';
	} else {
		return 'none';
	}
}

/**
 *
 * @param {Object} opts
 * @param {Array} opts.agents
 * @param {Boolean} opts.extended
 * @param {Boolean} opts.isModal
 * @param {Boolean} opts.hasPromotedAgent
 * @returns {String} - html
 */
export function renderResultAgentStats (opts) {
	const { agents, index, extended, isModal, hasPromotedAgent } = opts;
	const nbVisiblePreview = 2;
	const numberOfAgents = Object.entries(agents).length;

	let offset = 0;
	let html = '';

	if (extended || isModal) {
		const agent = js_params?.module?.Filters?.agencies?.[agents[index].agent] || agents[index].agent;
		html += renderResultAgentAdditional(agent);
	} else {
		if (hasPromotedAgent) {
			offset = 1;
		}

		if (index === 0 && numberOfAgents > 1 && hasPromotedAgent) {
			const agent = js_params?.module?.Filters?.agencies?.[agents[index].agent] || agents[index].agent;

			html += renderResultAgentAdditional(agent);
		} else {
			if (agents.length > 1 + offset) {
				html += renderResultAgentPreview(agents, offset, nbVisiblePreview);
			}

			if (agents.length >= offset + nbVisiblePreview) {
				html += renderResultAgentShowAll(agents, offset);
			}
		}
	}
	return html;
}

/**
	 * @param {String} agent
	 * @returns {String} - html
	 */
function renderResultAgentAdditional (agent) {
	return `<div class="abResult__additionalAgencyText abResult__additionalAgencyText--variation">${l('showresult.result.agenciesAvailableAt')}: <span>${agent}</span></div>`;
}

/**
 * @param {Array} agents
 * @param {Number} offset
 * @param {Number} numberVisiblePreview
 * @returns {String} - html
 */
function renderResultAgentPreview (agents, offset, numberVisiblePreview) {
	return `
<div class="agentPreview">
	${l('global.simplePhrases.alsoAt')}: ${agents.map(agent => `<span class="agentName">${agent.agent}</span>`).slice(offset + 1, offset + numberVisiblePreview + 1).join(', ')}
	${agents.length > offset + numberVisiblePreview + 1 ? '...' : ''}
</div>`;
}

/**
 * @param {Array} agents
 * @param {Number} offset
 * @returns {String} - html
 */
function renderResultAgentShowAll (agents, offset = 0) {
	return `
<div class="showAllAgencies">
	${lreplace('showresult.result.showAllAgencyCount', { agencyCount: agents.length - 1 - offset })}
	<span class="icon-chevron-down">
</div>
	`;
}

/**
 *
 * @param {String} agentLabel
 * @param {String|undefined} bestValue
 * @returns {String} - html
 */
export function renderResultAgentLabel (agentLabel, bestValue) {
	let html = '';
	switch (agentLabel) {
		case 'best': {
			const valueText = (bestValue === 'flexTicket') ? l('global.simplePhrases.rebooking') : l(`global.simplePhrases.${bestValue}`);
			const isOneAlreadyActive = resultsRender.isBaggageIncludedFiltered || resultsRender.isCabinBaggageIncludedFiltered || resultsRender.isFlexTicketFiltered;
			let tooltip = '';
			if (bestValue === 'flexTicket' || bestValue === 'baggageIncluded' || bestValue === 'cabinBaggageIncluded') {
				const translationKey = (bestValue === 'flexTicket') ? 'showresult.filter.flexTicketToolTip' : `searchBox.${bestValue.replace('Included', '')}.restriction`;
				tooltip = `
<div class="abBuyBtn__labelTooltip">
	<span class="icon icon-info" data-tooltip="${l(translationKey)}"></span>
</div>
`;
			}

			html = `
<div class="abBuyBtn__bestLabel abBuyBtn__bestLabel--${bestValue}">
	<img src="${linkPrefix.assetPrefix}/images/global/best_medal.svg" alt="Decorative medal for label tag background start" class="abBuyBtn__bestLabel--medal">
	<img src="${linkPrefix.assetPrefix}/images/global/best__${bestValue}.svg" alt="Decorative medal for label ${bestValue}" class="abBuyBtn__bestLabel--detail">
	${tooltip}
	<div class="abBuyBtn__bestLabelContent abBuyBtn__bestLabelContent--${bestValue}"${insertIf(isOneAlreadyActive, 'style="z-index: 1"')}>
		<span ${insertIf(isOneAlreadyActive, 'style="text-transform:lowercase"')}>${insertIf(isOneAlreadyActive, l('global.simplePhrases.and'))} ${valueText}</span>
	</div>
</div>
`;
			break;
		}

		case 'flex': {
			html = `
<div class="abBuyBtn__bestLabel abBuyBtn__bestLabel--flexTicket">
	<img src="${linkPrefix.assetPrefix}/images/global/best_medal.svg" alt="Decorative medal for label tag background start" class="abBuyBtn__bestLabel--medal">
	<img src="${linkPrefix.assetPrefix}/images/global/best__flexTicket.svg" alt="Decorative medal for label flexTicket" class="abBuyBtn__bestLabel--detail">
	<div class="abBuyBtn__labelTooltip">
		<span class="icon icon-info" data-tooltip="${l('global.simplePhrases.rebooking')}"></span>
	</div>
	<div class="abBuyBtn__bestLabelContent abBuyBtn__bestLabelContent--flexTicket">
		<span>${l(`global.simplePhrases.rebooking`)}</span>
	</div>
</div>
`;
			break;
		}
		case 'baggage': {
			html = `
<div class="abBuyBtn__bestLabel abBuyBtn__bestLabel--baggageIncluded">
	<img src="${linkPrefix.assetPrefix}/images/global/best_medal.svg" alt="Decorative medal for label tag background start" class="abBuyBtn__bestLabel--medal">
	<img src="${linkPrefix.assetPrefix}/images/global/best__baggageIncluded.svg" alt="Decorative medal for label baggageIncluded" class="abBuyBtn__bestLabel--detail">
	<div class="abBuyBtn__labelTooltip">
		<span class="icon icon-info" data-tooltip="${l('global.simplePhrases.baggageIncluded')}"></span>
	</div>
	<div class="abBuyBtn__bestLabelContent abBuyBtn__bestLabelContent--baggageIncluded">
		<span>${l(`global.simplePhrases.baggageIncluded`)}</span>
	</div>
</div>
`;
			break;
		}
		case 'cabinBaggage': {
			html = `
<div class="abBuyBtn__bestLabel abBuyBtn__bestLabel--cabinBaggage">
	<img src="${linkPrefix.assetPrefix}/images/global/best_medal.svg" alt="Decorative medal for label tag background start" class="abBuyBtn__bestLabel--medal">
	<img src="${linkPrefix.assetPrefix}/images/global/best__cabinBaggage.svg" alt="Decorative medal for label cabinBaggageIncluded" class="abBuyBtn__bestLabel--detail">
	<div class="abBuyBtn__labelTooltip">
		<span class="icon icon-info" data-tooltip="${l(`global.simplePhrases.cabinBaggageIncluded`)}"></span>
	</div>
	<div class="abBuyBtn__bestLabelContent abBuyBtn__bestLabelContent--cabinBaggageIncluded">
		<span>${l(`global.simplePhrases.cabinBaggageIncluded`)}</span>
	</div>
</div>
`;
			break;
		}
		case 'recommended': {
			html = `
<div class="abBuyBtn__recommendedLabel">
	<img src="${linkPrefix.assetPrefix}/images/global/recommend-head.png" alt="">
	<div class="abBuyBtn__recommendedLabelContent">
		<span>${l('global.simplePhrases.recommended')}</span>
	<div class="recommended-popup">
		<span>${l('showresult.recommendedPopup')}</span>
	</div>
	</div>
	<img src="${linkPrefix.assetPrefix}/images/global/recommend-tail.png" alt="">
</div>
`;
			break;
		}
	}

	return html;
}
